// Global authentication error handler and cross-tab logout coordination
import type { AuthContextType } from '@/features/auth/auth';
import { msalInstance } from '@/features/auth/msalInstance.ts';
import { router } from '@/router';
import { LOGOUT_EVENT_KEY } from '@/features/auth/constants.ts';

let authContext: AuthContextType | null = null;
let isLoggingOut = false; // Prevent multiple concurrent logout attempts

/**
 * Initialize the authentication error handler with auth context.
 */
export const initAuthErrorHandler = (auth: AuthContextType) => {
  authContext = auth;
  setupCrossTabListener();
};

/**
 * Clean up event listeners.
 */
export const cleanupAuthErrorHandler = () => {
  window.removeEventListener('storage', handleStorageEvent);
  authContext = null;
};

/**
 * Handle 401 authentication errors globally.
 * Call this from HTTP interceptors / error handlers.
 */
export const handleAuthenticationError = async (error?: unknown) => {
  console.error('Authentication error detected:', error);

  if (!authContext) {
    console.warn('Auth context not initialized, clearing cache and reloading');
    await clearMsalCacheAndReload();
    return;
  }

  console.log('Attempting to refresh authentication token...');
  try {
    // Force a real refresh — the server just rejected our token, so serving
    // from the MSAL cache would hand back the same invalid token.
    const token = await authContext.getAccessToken(true);
    if (token) {
      console.log('Token refresh successful, continuing...');
      return;
    }
  } catch (refreshError) {
    console.error('Token refresh failed:', refreshError);
  }

  console.log('Token refresh failed, triggering global logout...');
  triggerGlobalLogout();
};

/**
 * Trigger logout and notify all open tabs.
 */
export const triggerGlobalLogout = () => {
  console.log('Triggering global logout...');
  try {
    window.localStorage.setItem(LOGOUT_EVENT_KEY, Date.now().toString());
  } catch (error) {
    console.error('Failed to set logout event in localStorage:', error);
  }
  performLogout();
};

/**
 * Clear all MSAL-managed cache entries and reload.
 */
export const clearMsalCacheAndReload = async () => {
  console.log('Clearing MSAL cache and reloading...');
  try {
    // msalInstance.clearCache() is the official v5 API — no manual key iteration needed
    await msalInstance.clearCache();
    console.log('MSAL cache cleared');
  } catch (error) {
    console.error('Error clearing MSAL cache:', error);
  }
  window.location.reload();
};

/**
 * Perform logout cleanup and redirect.
 */
const performLogout = async () => {
  if (isLoggingOut) {
    console.log('Logout already in progress, skipping...');
    return;
  }
  isLoggingOut = true;

  try {
    console.log('Performing cross-tab logout cleanup...');
    try {
      await msalInstance.clearCache();
      console.log('MSAL cache cleared');
    } catch (error) {
      console.error('Error clearing MSAL cache:', error);
    }
    // Navigate via the router when possible (same-tab session expiry),
    // fall back to a full reload for cross-tab events where the router
    // may not be in a reliable state.
    try {
      await router.navigate({ to: '/sign-in', replace: true });
    } catch {
      window.location.href = '/';
    }
  } catch (error) {
    console.error('Error during logout:', error);
    window.location.href = '/';
  } finally {
    isLoggingOut = false;
  }
};

/** Set up cross-tab communication via storage events. */
const setupCrossTabListener = () => {
  window.removeEventListener('storage', handleStorageEvent); // avoid duplicates
  window.addEventListener('storage', handleStorageEvent);
};

const handleStorageEvent = (event: StorageEvent) => {
  if (event.key === LOGOUT_EVENT_KEY && event.newValue) {
    console.log('Logout event detected from another tab');
    // Use a full page reload instead of router.navigate to avoid the stale
    // router context causing a brief redirect loop (_auth beforeLoad may still
    // read isAuthenticated=true before React re-renders with the cleared state).
    void msalInstance.clearCache().finally(() => {
      window.location.href = '/';
    });
  }
};
